import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../shared/components/Button';
import { supabaseClient } from '../../shared/utils/supabaseClient';
import { useRefetchOnActive } from '../../shared/hooks/useRefetchOnActive';
import { checkFeatureFlag, FEATURE_FLAGS } from '../../shared/utils/featureFlags';
import { formatCurrency } from '../../shared/utils/formatCurrency';
import ReportProblemButton from '../support/components/ReportProblemButton';
import GroupSettingsModal from '../group-setup/components/GroupSettingsModal';
import InviteMemberModal from '../group-setup/components/InviteMemberModal';
import DeleteGroupModal from './components/DeleteGroupModal';
import { runGroupDeletion } from '../../shared/utils/groupDeletion';

// TODO: Enforce tier gating — Free Forever tier allows 1 group only.
// When subscription/tier data is available, replace `canCreateGroup = true`
// with a real check against the admin's plan tier before allowing creation.

const PaymentTrackingDashboard = () => {
  const [flagEnabled, setFlagEnabled] = useState(false);
  const [flagLoading, setFlagLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [settingsGroup, setSettingsGroup] = useState(null);
  const [inviteGroup, setInviteGroup] = useState(null);
  const [deleteGroup, setDeleteGroup] = useState(null);   // archived group -> permanent-delete modal
  const [restoringId, setRestoringId] = useState(null);
  const [notice, setNotice] = useState(null);
  const navigate = useNavigate();

  // Active vs archived split (Stage 39). Archived groups drop out of the main view.
  const activeGroups = groups.filter(g => !g.archived_at);
  const archivedGroups = groups.filter(g => g.archived_at);
  const refetch = () => { if (adminUser) fetchData(adminUser.id, { silent: true }); };

  const handleRestore = async (groupId) => {
    setRestoringId(groupId); setNotice(null);
    try {
      await runGroupDeletion(groupId, 'restore');
      refetch();
    } catch (err) {
      setNotice(`Could not restore the group: ${err.message || 'error'}`);
    } finally {
      setRestoringId(null);
    }
  };

  const handlePermanentlyDeleted = (groupId, purge) => {
    setDeleteGroup(null);
    setNotice(purge?.error
      ? 'Group deleted. Some proof files could not be removed from storage; they are harmless leftovers.'
      : 'Group permanently deleted. Attestations and the audit trail are preserved.');
    refetch();
  };

  const handleSignOut = async () => {
    await supabaseClient.auth.signOut();
    localStorage.clear();
    navigate('/');
  };

  useEffect(() => {
    const fetchAdminUser = async () => {
      const { data: { user } } = await supabaseClient.auth.getUser();
      if (user) setAdminUser(user);
    };
    fetchAdminUser();
  }, []);

  useEffect(() => {
    const checkFlag = async () => {
      const enabled = await checkFeatureFlag(FEATURE_FLAGS.PAYMENT_TRACKING);
      setFlagEnabled(enabled);
      setFlagLoading(false);
    };
    checkFlag();
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabaseClient.auth.getUser();
      if (!user) { navigate('/'); return; }
      fetchData(user.id);
    };
    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // In-flight guard so focus/visibility refetches never overlap.
  const fetchingRef = useRef(false);
  const fetchData = async (userId, { silent = false } = {}) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      if (!silent) setLoading(true);  // silent = background refetch, no spinner
      const { data: groupsData, error: groupsError } = await supabaseClient
        .from('groups')
        .select('*')
        .eq('billing_admin_id', userId);
      if (groupsError) throw groupsError;

      const groupIds = groupsData.map(g => g.id);
      const { data: membersData } = await supabaseClient
        .from('members')
        .select('group_id')
        .in('group_id', groupIds.length ? groupIds : [-1])
        .eq('status', 'active');

      setGroups(
        groupsData.map(g => ({
          ...g,
          memberCount: membersData?.filter(m => m.group_id === g.id).length || 0,
        }))
      );
    } catch {
      setError('Failed to load groups');
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  };

  // Refetch the group list when the tab/window regains focus or turns visible.
  useRefetchOnActive(() => { if (adminUser) fetchData(adminUser.id, { silent: true }); });

  const handleGroupUpdated = (updatedGroup) => {
    setGroups(prev => prev.map(g => g.id === updatedGroup.id ? { ...g, ...updatedGroup } : g));
    if (settingsGroup?.id === updatedGroup.id) {
      setSettingsGroup(prev => ({ ...prev, ...updatedGroup }));
    }
  };

  const handleCreateGroup = () => {
    // TODO: check tier — Free Forever allows 1 group only.
    // Replace `canCreate` with a real tier check when subscription data is available.
    const canCreate = true;
    if (!canCreate) {
      // Placeholder upgrade prompt (replace with modal when billing is wired)
      alert('Upgrade to Premium to manage multiple groups');
      return;
    }
    navigate('/onboarding');
  };

  if (flagLoading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;
  if (!flagEnabled) return <div className="flex items-center justify-center min-h-screen text-neutral-500">Feature not available</div>;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neutral-900" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-error-800">Error</h3>
          <p className="text-error-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-neutral-100 shadow-sm border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">Admin Dashboard</h1>
              <p className="text-sm text-neutral-600">
                Welcome back, {adminUser?.user_metadata?.full_name || adminUser?.email || 'Admin'}
              </p>
            </div>
            <Button onClick={handleSignOut} variant="secondary" size="sm">
              Sign Out
            </Button>
          </div>
        </div>
      </div>

      {/* Groups List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Section header + Create New Group */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-neutral-900">Your Groups</h2>
          <button
            onClick={handleCreateGroup}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-semibold hover:bg-primary-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Create New Group</span>
          </button>
        </div>

        {notice && (
          <div className="mb-4 bg-primary-50 border border-primary-200 rounded-lg px-4 py-3 text-sm text-primary-800 flex justify-between items-start">
            <span>{notice}</span>
            <button onClick={() => setNotice(null)} className="text-primary-400 hover:text-primary-600 ml-3">✕</button>
          </div>
        )}

        {activeGroups.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-neutral-600 mb-4">No groups yet. Create your first group to get started.</p>
            <button
              onClick={handleCreateGroup}
              className="px-6 py-3 bg-primary-600 text-neutral-50 rounded-lg font-semibold hover:bg-primary-700"
            >
              Create New Group
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeGroups.map((group) => (
              <div
                key={group.id}
                className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 flex flex-col"
              >
                {/* Card body — clicking navigates to payments */}
                <div
                  onClick={() => navigate(`/admin/group/${group.id}/payments`)}
                  className="p-6 flex-1 cursor-pointer hover:bg-neutral-50 transition-colors rounded-t-lg"
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-neutral-900 leading-tight pr-2">{group.group_name}</h3>
                    <span className={`flex-shrink-0 px-2 py-1 text-xs font-medium rounded-full ${
                      group.status === 'active' ? 'bg-success-100 text-success-800' :
                      group.status === 'paused' ? 'bg-warning-100 text-warning-800' :
                      'bg-neutral-100 text-neutral-800'
                    }`}>
                      {group.status || 'active'}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-neutral-500">Contribution / hand</span>
                      <span className="font-medium text-neutral-900">{formatCurrency(group.contribution_amount_per_hand)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-500">Frequency</span>
                      <span className="font-medium text-neutral-900 capitalize">{group.contribution_frequency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-500">Members</span>
                      <span className="font-medium text-neutral-900">{group.memberCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-500">Cycle</span>
                      <span className="font-medium text-neutral-900">#{group.cycle_number || 1}</span>
                    </div>
                  </div>
                </div>

                {/* Action bar — stops propagation so card click doesn't fire */}
                <div
                  className="flex border-t border-neutral-100"
                  onClick={e => e.stopPropagation()}
                >
                  <button
                    onClick={() => setSettingsGroup(group)}
                    className="flex-1 flex items-center justify-center space-x-1.5 py-3 text-xs font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 transition-colors border-r border-neutral-100 rounded-bl-lg"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Settings</span>
                  </button>
                  <button
                    onClick={() => setInviteGroup(group)}
                    className="flex-1 flex items-center justify-center space-x-1.5 py-3 text-xs font-medium text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors rounded-br-lg"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    <span>Invite Member</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Archived groups (Stage 39) — recoverable for 30 days, then permanently deletable. */}
        {archivedGroups.length > 0 && (
          <div className="mt-12">
            <h2 className="text-lg font-semibold text-neutral-700 mb-1">Archived groups</h2>
            <p className="text-xs text-neutral-500 mb-4">
              Recoverable for 30 days. Restore to make active again, or permanently delete.
            </p>
            <div className="space-y-3">
              {archivedGroups.map((group) => (
                <div key={group.id} className="bg-neutral-100 border border-neutral-200 rounded-lg px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-900">{group.group_name}</p>
                    <p className="text-xs text-neutral-500">
                      Archived {group.archived_at ? new Date(group.archived_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleRestore(group.id)}
                      disabled={restoringId === group.id}
                      className="px-3 py-1.5 border border-neutral-300 rounded-lg text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                    >
                      {restoringId === group.id ? 'Restoring…' : 'Restore'}
                    </button>
                    <button
                      onClick={() => setDeleteGroup(group)}
                      className="px-3 py-1.5 border border-error-300 text-error-700 rounded-lg text-xs font-medium hover:bg-error-50"
                    >
                      Delete permanently
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <ReportProblemButton />
        </div>
      </div>

      {/* Modals */}
      {settingsGroup && (
        <GroupSettingsModal
          group={settingsGroup}
          onClose={() => setSettingsGroup(null)}
          onGroupUpdated={handleGroupUpdated}
          onGroupArchived={() => refetch()}
          onGroupRemoved={() => { setNotice('Group deleted.'); refetch(); }}
        />
      )}
      {inviteGroup && (
        <InviteMemberModal
          group={inviteGroup}
          onClose={() => setInviteGroup(null)}
        />
      )}
      {deleteGroup && (
        <DeleteGroupModal
          group={deleteGroup}
          onClose={() => setDeleteGroup(null)}
          onDeleted={handlePermanentlyDeleted}
        />
      )}
    </div>
  );
};

export default PaymentTrackingDashboard;
